/**
 * Pure reporting-webhook business rules, ported from the Ember
 * `WebhookModel` + new/edit controllers so the invariants live in the data
 * layer (not the form). Kept side-effect-free and node-tested.
 */

/**
 * A webhook with no selected events cannot be enabled — mirrors the Ember
 * `WebhookModel.isForceDisabled` getter (`!this.events?.length`).
 */
export function isForceDisabled(events: readonly string[] | null | undefined): boolean {
  return events == null || events.length === 0;
}

/**
 * The save-time normalization both Ember controllers apply before every
 * `save()`: `if (!events?.length) enabled = false`. Reproduced here so the
 * mutation layer enforces it regardless of caller — the form is not the only
 * possible writer.
 */
export function normalizeEnabled(input: { enabled: boolean; events: readonly string[] | null | undefined }): boolean {
  return isForceDisabled(input.events) ? false : input.enabled;
}

/**
 * Project a loaded webhook back onto the write body.
 *
 * The create/update endpoint takes every field as required, so a caller that only means to flip
 * `enabled` still has to send the rest of the record — the same full-record PUT the Ember model's
 * `save()` produced. Callers override just the field they are changing:
 * `toWebhookWriteInput(webhook, { enabled: false })`.
 */
export function toWebhookWriteInput<T extends WebhookWriteFields>(
  webhook: T,
  overrides: Partial<WebhookWriteFields> = {},
): WebhookWriteFields {
  return {
    name: webhook.name,
    endpoint_url: webhook.endpoint_url,
    events: webhook.events,
    enabled: webhook.enabled,
    resolution: webhook.resolution,
    with_content: webhook.with_content,
    ...overrides,
  };
}

/** The write-body fields, structurally — kept local so this stays a pure, dependency-free module. */
type WebhookWriteFields = {
  name: string;
  endpoint_url: string;
  events: string[];
  enabled: boolean;
  resolution: boolean;
  with_content: boolean;
};
